import randomColor from 'randomcolor';
import React from 'react';
import { Pie } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';

import {
  CCASSQueryDetailsResponse,
  CCASSShareholdingDetail,
  CCASSSummary,
} from '../../../py-generated/datalouder/ccass/types';
import { getParticipantName } from './CCASSShareholdingTable';
import { NOT_IN_CCASS } from './CCASSSummaryTables';

interface Props {
  shareholdingSummary: CCASSSummary;
  shareholdingDetails: CCASSShareholdingDetail[];
  participants: CCASSQueryDetailsResponse['participants'];
}

const colors = randomColor({
  count: 16,
  hue: 'grey',
});

const combineItems = (items: ReadonlyArray<{ value: number }>): number => {
  return items.reduce((pre, cur) => cur.value + pre, 0);
};

const ShareholdingPieChart: React.FC<Props> = ({
  shareholdingSummary,
  shareholdingDetails,
  participants,
}) => {
  const { t, i18n } = useTranslation();

  // Algorithm to ranking participants refers to _calculate_concentration in fetch/fetch_ccass/derive.py:40
  const nonCCASS =
    shareholdingSummary.issued_shares - shareholdingSummary.total_ccass;

  const shareholdings = shareholdingDetails
    .map(item => ({
      label: getParticipantName(
        participants,
        item.participant_id,
        item.participant_name
      )[i18n.language],
      value: item.share_holding,
    }))
    .sort((a, b) => b.value - a.value);

  const coloredShareholdings = shareholdings.map((item, i) => ({
    ...item,
    color: colors[i],
  }));

  const allShareholdings = [
    ...coloredShareholdings,
    {
      label: t(NOT_IN_CCASS),
      value: nonCCASS,
      color: 'grey',
    },
  ].sort((a, b) => b.value - a.value);

  // only show the first 15
  const trimmed = [
    ...allShareholdings.slice(0, 15),
    {
      label: t('other-participants'),
      value: combineItems(allShareholdings.slice(15)),
      color: '#ccc',
    },
  ];

  const data = {
    labels: trimmed.map(({ label }) => label),
    datasets: [
      {
        data: trimmed.map(({ value }) => value),
        backgroundColor: trimmed.map(({ color }) => color),
        borderWidth: 1,
      },
    ],
  };

  return (
    <Pie
      data={data}
      options={{
        legend: {
          display: false,
        },
        title: {
          display: true,
          text: t('sharehold-distribution'),
        },
        responsive: true,
        maintainAspectRatio: true,
      }}
    />
  );
};

export default ShareholdingPieChart;
